// src/pages/MFASetup/MFASetupPage.tsx
import { FC, useContext, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { mfaService } from 'features/mfa';
import QRCode from 'react-qr-code';
import './MFASetupPage.scss';
import { UserContext } from "features/account";

const MFASetupPage: FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'email' | 'mobile'>('email');
  const [step, setStep] = useState<'choose' | 'setup' | 'verify'>('choose');
  const [code, setCode] = useState('');
  const [qrCode, setQrCode] = useState<string>('');
  const [secret, setSecret] = useState<string>('');
  const [mfaEmail, setMfaEmail] = useState<string>('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const location = useLocation();

  const { user} = useContext(UserContext);
  const type = user?.type ?? 'individual'

  useEffect(() => {
    const state = location.state as any;
    if (state?.mfaEmail && state?.mfaType) {
      setMfaEmail(state.mfaEmail);
      mfaService.resendEmailVerificationCode(state?.mfaEmail).then(r => {});
    } else {
      navigate(-1);
    }
  }, [location, navigate]);

  const handleSetupEmail = async () => {
    setLoading(true);
    setError('');
    try {
      await mfaService.setupEmailMFA(type);
      setStep('verify');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCodeEmail = async () => {
    setLoading(true);
    setError('');
    try {
      await mfaService.resendEmailVerificationCode(mfaEmail);
      setStep('verify');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSetupMobile = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await mfaService.setupMobileMFA(type);
      setQrCode(response.qrCode || '');
      setSecret(response.secret || '');
      setStep('setup');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erreur lors de la configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError('Veuillez entrer un code à 6 chiffres');
      return;
    }

    setLoading(true);
    setError('');
    try {
      const response = await mfaService.enableMFA(
        activeTab,
        code,
        activeTab === 'mobile' ? secret : undefined,
        type
      );
      setBackupCodes(response.backupCodes);
      setStep('verify');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Code invalide');
    } finally {
      setLoading(false);
    }
  };

  const handleComplete = () => {
    navigate('/account/security');
  };

  const renderChooseMethod = () => (
    <div className="choose-method">
      <h2>Choisissez votre méthode d'authentification</h2>

      <div className="method-cards">
        <div
          className={`method-card ${activeTab === 'email' ? 'selected' : ''}`}
          onClick={() => setActiveTab('email')}
        >
          <h3>Email</h3>
          <p>Recevez un code par email à chaque connexion</p>
        </div>

        <div
          className={`method-card ${activeTab === 'mobile' ? 'selected' : ''}`}
          onClick={() => setActiveTab('mobile')}
        >
          <h3>Application mobile</h3>
          <p>Utilisez Google Authenticator, Microsoft Authenticator, OneAuth, etc</p>
        </div>
      </div>

      <button
        className="btn-continue"
        onClick={activeTab === 'email' ? handleSetupEmail : handleSetupMobile}
        disabled={loading}
      >
        {loading ? 'Chargement...' : 'Continuer'}
      </button>

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  const renderMobileSetup = () => (
    <div className="mobile-setup">
      <h2>Configurez votre application d'authentification</h2>

      <div className="qr-container">
        {qrCode && (
          <>
            <img src={qrCode} alt="QR Code" />
            <p>Scannez ce QR code avec votre application</p>
          </>
        )}
      </div>

      <div className="secret-container">
        <p>Ou saisissez ce code manuellement :</p>
        <code>{secret}</code>
      </div>

      <button
        className="btn-next"
        onClick={() => setStep('verify')}
      >
        J'ai scanné le QR code
      </button>
    </div>
  );

  const renderVerify = () => (
    <div className="verify-code">
      <h2>Vérifiez votre code</h2>

      {activeTab === 'email' && (
        <p>Un code à 6 chiffres a été envoyé à votre adresse email</p>
      )}

      {activeTab === 'mobile' && (
        <p>Entrez le code à 6 chiffres généré par votre application</p>
      )}

      <div className="code-input">
        <input
          type="text"
          maxLength={6}
          placeholder="000000"
          value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
          autoFocus
        />
      </div>

      {backupCodes.length > 0 ? (
        <div className="backup-codes">
          <h3>Sauvegardez ces codes de secours</h3>
          <p>Ces codes vous permettront d'accéder à votre compte si vous perdez l'accès à votre méthode MFA.</p>
          <div className="codes-list">
            {backupCodes.map((backupCode, index) => (
              <div key={index} className="backup-code">{backupCode}</div>
            ))}
          </div>
          <button className="btn-copy" onClick={() => {
            navigator.clipboard.writeText(backupCodes.join('\n'));
            alert('Codes copiés dans le presse-papier');
          }}>
            Copier les codes
          </button>
          <button className="btn-complete" onClick={handleComplete}>
            Terminer
          </button>
        </div>
      ) : (
        <>
          <button
            className="btn-verify"
            onClick={handleVerify}
            disabled={loading || code.length !== 6}
          >
            {loading ? 'Vérification...' : 'Vérifier'}
          </button>
          <button
            className="btn-resend"
            onClick={activeTab === 'email' ? handleResendCodeEmail : undefined}
            disabled={loading}
          >
            Renvoyer le code
          </button>
        </>
      )}

      {error && <div className="error-message">{error}</div>}
    </div>
  );

  return (
    <div className="mfa-setup-page">
      <div className="container">
        {step === 'choose' && renderChooseMethod()}
        {step === 'setup' && activeTab === 'mobile' && renderMobileSetup()}
        {step === 'verify' && renderVerify()}
      </div>
    </div>
  );
};

export default MFASetupPage;